<template>
	<div class="form">
		<h4 class="step-title">
			<label for="zipCode">What is your address?</label>
		</h4>
		<ValidationObserver
			v-slot="{ errors, invalid, handleSubmit }"
			@submit.prevent
		>
			<div class="step-content d-flex flex-column align-items-center">
				<div class="step-item">
					<div class="form-group mb-0 row">
						<div class="col-12 col-md-6 mt-4">
							<ValidationProvider
								mode="eager"
								vid="streetAddress"
								name="Street address"
								rules="required"
							>
								<input
									id="streetAddress"
									class="form-control"
									placeholder="Street address"
									v-on:keyup.enter="enterKeyup"
									v-model="lead.streetAddress"
								/>
							</ValidationProvider>
							<div
								class="alert alert-danger mt-2"
								v-if="
									errors['streetAddress'] &&
									errors['streetAddress'].length > 0
								"
							>
								{{ errors["streetAddress"][0] }}
							</div>
						</div>
						<div class="col-12 col-md-6 mt-4">
							<ValidationProvider
								mode="eager"
								vid="city"
								name="City"
								rules="required"
							>
								<input
									id="city"
									class="form-control"
									placeholder="City"
									v-on:keyup.enter="enterKeyup"
									v-model="lead.city"
								/>
							</ValidationProvider>
							<div
								class="alert alert-danger mt-2"
								v-if="
									errors['city'] && errors['city'].length > 0
								"
							>
								{{ errors["city"][0] }}
							</div>
						</div>
						<div class="col-12 col-md-6 mt-4">
							<ValidationProvider
								mode="eager"
								vid="state"
								name="State"
								rules="required"
							>
								<select
									id="state"
									class="form-control"
									v-bind:class="{ placeholder: !lead.state }"
									v-model="lead.state"
								>
									<option disabled value="">State</option>
									<option
										v-for="state in states"
										v-bind:key="state.value"
										v-bind:value="state.value"
									>
										{{ state.text }}
									</option>
								</select>
							</ValidationProvider>
							<div
								class="alert alert-danger mt-2"
								v-if="
									errors['state'] &&
									errors['state'].length > 0
								"
							>
								{{ errors["state"][0] }}
							</div>
						</div>
						<div class="col-12 col-md-6 mt-4">
							<ValidationProvider
								mode="eager"
								vid="zipCode"
								name="Zip code"
								rules="required|digits:5"
							>
								<input
									id="zipCode"
									class="form-control"
									placeholder="ZIP code"
									v-on:keyup.enter="enterKeyup"
									v-model="lead.zipCode"
								/>
							</ValidationProvider>
							<div
								class="alert alert-danger mt-2"
								v-if="
									errors['zipCode'] &&
									errors['zipCode'].length > 0
								"
							>
								{{ errors["zipCode"][0] }}
							</div>
						</div>
					</div>
				</div>
				<div class="step-item">
					<button
						class="btn btn-step btn-lg form-control"
						ref="complete"
						v-on:click="handleSubmit(complete)"
					>
						Continue
					</button>
				</div>
			</div>
			<SlotInvalidInputWatchComponent
				v-bind:invalid="invalid"
				v-bind:value="lead.zipCode"
				v-on:invalidInput="onInvalidInput"
			/>
		</ValidationObserver>
	</div>
</template>

<script>
import { extend } from "vee-validate";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { digits, required } from "vee-validate/dist/rules";
import SlotInvalidInputWatchComponent from "./shared/SlotInvalidInputWatchComponent.vue";

extend("digits", {
	...digits,
	message: "Invalid zip code.",
});

extend("required", {
	...required,
	message: "{_field_} is required.",
});

export default {
	name: "AddressCityStateZipCode",
	components: {
		ValidationProvider,
		ValidationObserver,
		SlotInvalidInputWatchComponent,
	},
	data: function () {
		return {
			states: [
				{ value: "AK", text: "Alaska" },
				{ value: "AL", text: "Alabama" },
				{ value: "AR", text: "Arkansas" },
				{ value: "AZ", text: "Arizona" },
				{ value: "CA", text: "California" },
				{ value: "CO", text: "Colorado" },
				{ value: "CT", text: "Connecticut" },
				{ value: "DC", text: "District of Columbia" },
				{ value: "DE", text: "Delaware" },
				{ value: "FL", text: "Florida" },
				{ value: "GA", text: "Georgia" },
				{ value: "HI", text: "Hawaii" },
				{ value: "IA", text: "Iowa" },
				{ value: "ID", text: "Idaho" },
				{ value: "IL", text: "Illinois" },
				{ value: "IN", text: "Indiana" },
				{ value: "KS", text: "Kansas" },
				{ value: "KY", text: "Kentucky" },
				{ value: "LA", text: "Louisiana" },
				{ value: "MA", text: "Massachusetts" },
				{ value: "MD", text: "Maryland" },
				{ value: "ME", text: "Maine" },
				{ value: "MI", text: "Michigan" },
				{ value: "MN", text: "Minnesota" },
				{ value: "MO", text: "Missouri" },
				{ value: "MS", text: "Mississippi" },
				{ value: "MT", text: "Montana" },
				{ value: "NC", text: "North Carolina" },
				{ value: "ND", text: "North Dakota" },
				{ value: "NE", text: "Nebraska" },
				{ value: "NH", text: "New Hampshire" },
				{ value: "NJ", text: "New Jersey" },
				{ value: "NM", text: "New Mexico" },
				{ value: "NV", text: "Nevada" },
				{ value: "NY", text: "New York" },
				{ value: "OH", text: "Ohio" },
				{ value: "OK", text: "Oklahoma" },
				{ value: "OR", text: "Oregon" },
				{ value: "PA", text: "Pennsylvania" },
				{ value: "RI", text: "Rhode Island" },
				{ value: "SC", text: "South Carolina" },
				{ value: "SD", text: "South Dakota" },
				{ value: "TN", text: "Tennessee" },
				{ value: "TX", text: "Texas" },
				{ value: "UT", text: "Utah" },
				{ value: "VA", text: "Virginia" },
				{ value: "VT", text: "Vermont" },
				{ value: "WA", text: "Washington" },
				{ value: "WI", text: "Wisconsin" },
				{ value: "WV", text: "West Virginia" },
				{ value: "WY", text: "Wyoming" },
			],
		};
	},
	props: {
		lead: Object,
	},
	methods: {
		enterKeyup: function () {
			this.$refs.complete.click();
		},
		complete: function () {
			this.$emit("complete", this.$options.name, this.lead.zipCode);
		},
		onInvalidInput: function (value) {
			this.utils.log("onInvalidInput: " + value);
			this.$emit("invalidInput", this.$options.name, value);
		},
	},
};
</script>

<style scoped>
.placeholder {
	color: #6c757d;
}
</style>
